/**
 * GraphQL Stream Viewer Helper Library
 * Enables live SSE event monitoring for the GraphQL Stream Viewer extension
 */

import type { HelperConfig, HelperConnection } from './types';
import {
	installEventSourceWrapper,
	uninstallEventSourceWrapper,
} from './eventsource-wrapper';
import { isExtensionAvailable, waitForExtension } from './messenger';

export type { HelperConfig, HelperConnection, SSEEventData, ConnectionEvent } from './types';

const DEFAULT_CONFIG: Required<HelperConfig> = {
	captureSSE: true,
	captureHTTP: false,
	debug: false,
	urlFilter: (url: string) => {
		const lowerUrl = url.toLowerCase();
		return (
			lowerUrl.includes('/graphql') ||
			lowerUrl.includes('/gql') ||
			lowerUrl.includes('query=')
		);
	},
};

let currentConnection: HelperConnection | null = null;

/**
 * Connect to the GraphQL Stream Viewer extension
 * This enables live SSE event monitoring
 *
 * @param config - Configuration options
 * @returns A connection object with disconnect method
 *
 * @example
 * ```typescript
 * import { connectDevTools } from '@graphql-stream-viewer/helper';
 *
 * // Connect with default config
 * const connection = connectDevTools();
 *
 * // Later, when you want to disconnect
 * connection.disconnect();
 * ```
 *
 * @example
 * ```typescript
 * // Connect with custom config
 * const connection = connectDevTools({
 *   debug: true,
 *   captureHTTP: true,
 *   urlFilter: (url) => url.includes('/api/graphql')
 * });
 * ```
 */
export function connectDevTools(
	config: HelperConfig = {},
): HelperConnection {
	// Merge with defaults
	const finalConfig: Required<HelperConfig> = {
		...DEFAULT_CONFIG,
		...config,
	};

	// Don't connect multiple times
	if (currentConnection) {
		if (finalConfig.debug) {
			console.warn(
				'[GraphQL Stream Viewer Helper] Already connected. Call disconnect() first.',
			);
		}
		return currentConnection;
	}

	if (finalConfig.debug) {
		console.log('[GraphQL Stream Viewer Helper] Connecting...', finalConfig);
	}

	// Install EventSource wrapper
	if (finalConfig.captureSSE) {
		installEventSourceWrapper(finalConfig);
	}

	// TODO: Install fetch/XHR wrapper for HTTP requests
	if (finalConfig.captureHTTP) {
		if (finalConfig.debug) {
			console.warn(
				'[GraphQL Stream Viewer Helper] HTTP capture not yet implemented',
			);
		}
	}

	// Create connection object
	const connection: HelperConnection = {
		disconnect: () => {
			if (finalConfig.captureSSE) {
				uninstallEventSourceWrapper();
			}

			if (finalConfig.debug) {
				console.log('[GraphQL Stream Viewer Helper] Disconnected');
			}

			currentConnection = null;
		},

		isConnected: () => {
			return currentConnection === connection;
		},

		getConfig: () => {
			return finalConfig;
		},
	};

	currentConnection = connection;

	if (finalConfig.debug) {
		console.log('[GraphQL Stream Viewer Helper] Connected successfully');
	}

	return connection;
}

/**
 * Disconnect from the GraphQL Stream Viewer extension
 * This removes all wrappers and stops capturing events
 */
export function disconnect(): void {
	if (currentConnection) {
		currentConnection.disconnect();
	}
}

/**
 * Check if currently connected to the extension
 */
export function isConnected(): boolean {
	return currentConnection?.isConnected() ?? false;
}

/**
 * Check if the GraphQL Stream Viewer extension is available
 * Note: This is a best-effort check
 */
export function checkExtensionAvailable(): boolean {
	return isExtensionAvailable();
}

/**
 * Wait for the extension to become available (with timeout)
 * Useful for ensuring the extension is loaded before connecting
 *
 * @param timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns Promise that resolves to true if extension is available
 *
 * @example
 * ```typescript
 * import { waitForExtension, connectDevTools } from '@graphql-stream-viewer/helper';
 *
 * async function setupDevTools() {
 *   const available = await waitForExtension();
 *   if (available) {
 *     connectDevTools({ debug: true });
 *   }
 * }
 * ```
 */
export { waitForExtension };

/**
 * Get the current connection, if any
 */
export function getConnection(): HelperConnection | null {
	return currentConnection;
}
